  // src/components/TimerLogic.js
  import { useState, useRef, useEffect, useCallback } from "react";

  /**
   * Custom React hook for highly accurate timers (stopwatch & countdown)
   *
   * @param {Object} params
   * @param {boolean} params.running   - Is the timer running?
   * @param {"stopwatch"|"timer"} params.mode - Timer mode: "stopwatch" or "timer"
   * @param {number} params.baseSeconds - Initial seconds (for countdown)
   * @param {function=} params.onExpire - Callback when countdown hits zero
   * @returns {Object}
   */
  export function useAccurateTimer({
    running,
    mode = "timer",
    baseSeconds = 0,
    onExpire,
  }) {
    const [timerSeconds, setTimerSeconds] = useState(baseSeconds);
    const [timerMs, setTimerMs] = useState(baseSeconds * 1000);

    // Keep these refs for absolute accuracy
    const startTimestampRef = useRef(null);
    const pauseElapsedRef = useRef(0);
    const intervalRef = useRef(null);

    // Reset on mode/baseSeconds change
    useEffect(() => {
      setTimerSeconds(baseSeconds);
      setTimerMs(baseSeconds * 1000);
      startTimestampRef.current = null;
      pauseElapsedRef.current = 0;
    }, [mode, baseSeconds]);

    // Main timing logic
    useEffect(() => {
      if (running) {
        // Only set startTimestamp if timer is not already running
        if (!startTimestampRef.current) {
          startTimestampRef.current = Date.now();
        }
        intervalRef.current = setInterval(() => {
          const now = Date.now();
          let elapsed = now - startTimestampRef.current + pauseElapsedRef.current;
    
          if (mode === "stopwatch") {
            setTimerSeconds(Math.floor(elapsed / 1000));
            setTimerMs(elapsed);
          } else {
            const remaining = Math.max(baseSeconds * 1000 - elapsed, 0);
            setTimerSeconds(Math.floor(remaining / 1000));
            setTimerMs(remaining);
    
            if (remaining <= 0) {
              clearInterval(intervalRef.current);
              if (onExpire) onExpire();
            }
          }
        }, 16);
        return () => clearInterval(intervalRef.current);
      } else {
        // Only do this if we are transitioning from running to stopped
        if (startTimestampRef.current) {
          pauseElapsedRef.current += Date.now() - startTimestampRef.current;
          startTimestampRef.current = null;
        }
        clearInterval(intervalRef.current);
      }
      // eslint-disable-next-line
    }, [running, mode, baseSeconds, onExpire]);
    
    // Reset to baseSeconds, for when you hit "reset"
    const reset = useCallback(
      (newBaseSeconds = baseSeconds) => {
        setTimerSeconds(newBaseSeconds);
        setTimerMs(newBaseSeconds * 1000);
        startTimestampRef.current = null;
        pauseElapsedRef.current = 0;
      },
      [baseSeconds]
    );

    return {
      timerSeconds, // integer, for display
      timerMs,      // millisecond precision if you want it
      setTimerSeconds,
      reset,
    };
  }
